import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { RichTextContent, EditableSlotGroup, useSlotGroup, useTokens } from '@vibesites/runtime/react';
import { relativeLuminance } from '../utils/luminance';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { CTA_SOLID, CTA_SURFACE_HOVER, CtaStyles, ctaOverLight } from './_cta';

// A plain-string heading from pages.json is wrapped in <p>…</p> so it matches
// the DOM shape RichTextContent expects, escaping HTML special characters so a
// heading containing `<` / `&` can't poison the markup.
function toRichTextDefault(text: string | undefined | null): string {
  if (!text) return '';
  const escaped = String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<p>${escaped}</p>`;
}

interface PageBlurbProps {
  /** Property field name for the blurb (e.g. 'property_floorplans_blurb') */
  blurbField?: string;
  /** Unique prefix so each page's blurb token is independently editable */
  tokenPrefix?: string;
  /** Static fallback text when no property blurb is available */
  defaultText?: string;
  /** Optional SEO heading (rendered as an <h2>) above the blurb copy */
  heading?: string;
  /** Optional CTA button rendered below the blurb (omit for a text-only blurb) */
  ctaLabel?: string;
  /** Destination for the optional CTA button */
  ctaUrl?: string;
}

export function PageBlurb({ blurbField, tokenPrefix, defaultText, heading, ctaLabel, ctaUrl }: PageBlurbProps) {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const prefix = tokenPrefix || 'pageblurb';
  const headingPath = `${prefix}.heading`;
  const textPath = `${prefix}.text`;
  const ctaPath = `${prefix}.cta_link`;

  // Per-instance CTA defaults come from props so each page can point its button
  // at a different verb/target. The static contentSchema below keeps canonical
  // (empty) defaults; the hidden anchor satisfies the schema linter.
  const ctaLinkDef: SlotGroupDefinition = {
    label: 'CTA Link',
    fields: {
      url: { label: 'URL', type: 'url', defaultValue: ctaUrl || '' },
      label: { label: 'Link Text', type: 'text', defaultValue: ctaLabel || '' },
      _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    },
  };

  const cta = useSlotGroup(ctaPath, ctaLinkDef);
  const ctaText = cta.label || ctaLabel;
  const ctaHref = cta.url || ctaUrl;

  // Match PropertyOverview's title color logic: when the theme's on-primary
  // text color is dark, the property has a light primary — a light-primary
  // heading reads too faint, so fall back to solid black. Otherwise keep the
  // branded primary color.
  const tokens = useTokens();
  const titleColor =
    relativeLuminance(tokens?.color?.textOnPrimary) < 0.5
      ? '#000000'
      : 'var(--vibe-color-primary)';

  return (
    <section ref={sectionRef} className="reveal-fade-up pt-10 pb-4 md:pt-14 md:pb-6 bg-[var(--vibe-color-background)]">
      <CtaStyles />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {heading ? (
          <RichTextContent
            path={headingPath}
            defaultValue={toRichTextDefault(heading)}
            as="h2"
            className="font-heading text-[32px] font-bold mb-6 leading-[1.1] [text-wrap:balance] md:whitespace-nowrap [&_p]:m-0 [&_p]:[text-wrap:balance] md:[&_p]:whitespace-nowrap"
            style={{ color: titleColor }}
          />
        ) : null}

        <RichTextContent
          path={textPath}
          defaultField={blurbField}
          defaultValue={defaultText || ''}
          as="div"
          className="font-body text-base sm:text-lg leading-relaxed text-[var(--vibe-color-text)]/75 [&_a]:text-primary [&_a]:underline [&_p]:mb-4 [&_p:last-child]:mb-0"
        />

        {ctaText ? (
          <div className="mt-8 md:mt-10">
            <EditableSlotGroup groupPath={ctaPath} definition={ctaLinkDef}>
              <a
                href={ctaHref || '#'}
                target={cta._target || '_self'}
                className={`${CTA_SOLID} ${CTA_SURFACE_HOVER}`}
                style={ctaOverLight}
              >
                <span className="v-cta-label">{ctaText}</span>
              </a>
            </EditableSlotGroup>
          </div>
        ) : null}
      </div>

      {/* Schema-lint anchors for static analysis — hidden at runtime */}
      <RichTextContent
        path="pageblurb.heading"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="pageblurb.text"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="pageblurb.cta_link"
        definition={ctaLinkDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PageBlurb',
  displayName: 'Page Blurb',
  tokens: {
    'pageblurb.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '',
    },
    'pageblurb.text': {
      label: 'Blurb Text',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'pageblurb.cta_link': {
      label: 'CTA Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '' },
        label: { label: 'Link Text', type: 'text', defaultValue: '' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
};

(PageBlurb as any).contentSchema = contentSchema;

export default PageBlurb;
